import { useCallback, useEffect, useMemo } from 'react'
import { isPlatform, useIonRouter } from '@ionic/react'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { LocalNotifications } from '@capacitor/local-notifications'
import { useQueryClient } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'

import { chatQueryKeys } from '../hooks/chat/queries'
import {
  isNotificationData,
  isConversationNotification,
  isClientNotification,
  isCheckInNotification,
} from './utils'

function useNotificationListeners() {
  const history = useHistory()
  const router = useIonRouter()

  const { composeNotificationPath } = useComposeNotificationPath()

  const swMessageHandler = useCallback(
    (event: MessageEvent<any>) => {
      console.log(`serviceWorker message`, { event })
      const data = event.data.data

      if (isCheckInNotification(data)) {
        console.log('foreground sw check-in received')
        const url = `${
          router.routeInfo.pathname.includes('tabs') ? '/tabs' : ''
        }/clients/client/${data.clientId}/redeem-loyalty`

        if (router.routeInfo.pathname.includes('redeem-loyalty')) {
          history.replace(url)
        } else {
          history.push(url)
        }
      }
    },
    [history, router.routeInfo.pathname]
  )

  useEffect(() => {
    FirebaseMessaging.addListener('tokenReceived', (event) => {
      console.log('tokenReceived', event)
      // update installation??
    })

    FirebaseMessaging.addListener('notificationReceived', (event) => {
      console.log('notificationReceived', event)
      if (isCheckInNotification(event.notification.data)) {
        console.log('foreground check-in received')
        const url = `${
          router.routeInfo.pathname.includes('tabs') ? '/tabs' : ''
        }/clients/client/${event.notification.data.clientId}/redeem-loyalty`

        if (router.routeInfo.pathname.includes('redeem-loyalty')) {
          history.replace(url)
        } else {
          history.push(url)
        }
      }

      if (isPlatform('android')) {
        console.log('android scheduling LocalNotification')
        LocalNotifications.schedule({
          notifications: [
            {
              title: event.notification.title ?? '',
              body: event.notification.body ?? '',
              id: 1,
              extra: event.notification.data,
            },
          ],
        })
      }
    })

    LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (event) => {
        console.log(
          'localNotificationActionPerformed',
          event.notification.extra
        )
        const path = composeNotificationPath(event.notification.extra)
        if (!path) return
        if (history.length === 1) {
          history.replace(path)
        } else {
          history.push(path)
        }
      }
    )

    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      console.log('notificationActionPerformed', event)
      const path = composeNotificationPath(event.notification.data)
      if (!path) return
      if (history.length === 1) {
        history.replace(path)
      } else {
        history.push(path)
      }
    })

    // this will still run on native devices when app is in browser (caused problems with heartland UI extension)
    if (!Capacitor.isNativePlatform()) {
      navigator?.serviceWorker?.addEventListener('message', swMessageHandler)
    }

    return () => {
      FirebaseMessaging.removeAllListeners()
      if (!Capacitor.isNativePlatform())
        navigator?.serviceWorker?.removeEventListener(
          'message',
          swMessageHandler
        )
    }
  }, [
    composeNotificationPath,
    history,
    router.routeInfo.pathname,
    swMessageHandler,
  ])
}

function useComposeNotificationPath() {
  const queryClient = useQueryClient()

  const router = useIonRouter()

  return useMemo(
    () => ({
      composeNotificationPath: (n: unknown) => {
        let path: string = ''

        if (isNotificationData(n)) {
          const notificationDetail = JSON.parse(n.notification)

          const likesClientId = notificationDetail.likes?.[0]?.client?.objectId
          if (likesClientId) {
            path = `/tabs/clients/client/${likesClientId}?currentview=wishlist`
          }
        } else if (isConversationNotification(n)) {
          // invalidate conversation and refetch
          queryClient.invalidateQueries(
            chatQueryKeys.conversationDetail(n.conversationId)
          )
          path = `/chat/c/${n.conversationId}`
          // checkIn must come before client since client always passes if there is a clientId
        } else if (isCheckInNotification(n)) {
          path = `${
            router.routeInfo.pathname.includes('tabs') ? '/tabs' : ''
          }/clients/client/${n.clientId}/redeem-loyalty`
        } else if (isClientNotification(n)) {
          path = `/tabs/clients/client/${n.clientId}`
        }

        return path
      },
    }),
    [queryClient, router.routeInfo.pathname]
  )
}
export { useNotificationListeners }
